import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  Check,
  PenLine,
  Mail,
  Trash2,
  FileText,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ExternalLink,
  RotateCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  getSigningRequest,
  submitSigningRequest,
  approveCooSigningRequest,
  rejectSigningRequest,
  deleteSigningRequest,
  fetchSigningPdfBlob,
} from "../../../api/hrm/signing/signingRequests";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
  showInputDialog,
} from "../../../components/ConfirmDialog";
import { formatDateVN } from "../../../utils/formatUtils";
import SigningNotifyModal from "./SigningNotifyModal";
import SigningSendEmailModal from "./SigningSendEmailModal";
import { STATUS_BADGE, STATUS_LABEL, ACTION_LABEL, DOCUMENT_TYPE_LABEL, URGENCY_LABEL } from "./signingConstants";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_BASE_WIDTH = 460;
const PDF_ZOOM_MIN = 0.6;
const PDF_ZOOM_MAX = 2.5;
const PDF_ZOOM_STEP = 0.15;

const btnPrimary =
  "inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap px-5 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-red-700 to-red-800 text-white hover:shadow-lg hover:shadow-red-100 transition-all";
const btnSecondary =
  "inline-flex h-11 items-center justify-center whitespace-nowrap px-4 rounded-full text-xs font-bold uppercase tracking-wider border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 bg-white transition-all";
const btnDanger =
  "inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap px-4 rounded-full text-xs font-bold uppercase tracking-wider border border-red-200 text-red-600 hover:bg-red-50 transition-all";
const pdfToolBtn =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:pointer-events-none transition-all";

const SigningRequestDetailModal = ({ requestId, open, onClose, onUpdated }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [downloadingSigned, setDownloadingSigned] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [pdfVariant, setPdfVariant] = useState("original");
  const [pdfRequested, setPdfRequested] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const pdfScrollRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 1024); // lg breakpoint
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const load = async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      setDetail(await getSigningRequest(requestId));
    } catch {
      showErrorToast("Không tải được chi tiết.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && requestId) load();
  }, [open, requestId]);

  const resetPdfViewer = () => {
    setPdfNumPages(null);
    setPdfPage(1);
    setPdfZoom(1);
    setPdfRotation(0);
  };

  const loadPdfPreview = async (variant = "original") => {
    if (!requestId) return;
    // Cleanup old object URL (avoid memory leak)
    setPdfUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
    setPdfLoading(true);
    resetPdfViewer();
    setPdfVariant(variant);
    try {
      const buf = await fetchSigningPdfBlob(requestId, variant);
      const url = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      setPdfUrl(url);
    } catch {
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // Desktop render preview ngay; mobile chỉ render khi user bấm "Xem file"
  useEffect(() => {
    if (!open) {
      setPdfRequested(false);
      setPdfVariant("original");
      resetPdfViewer();
      return;
    }
    if (!isMobile) {
      setPdfRequested(true);
    }
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !requestId || !pdfRequested || !detail) return;
    loadPdfPreview(detail.signed_file_path ? "signed" : "original");

    return () => {
      setPdfUrl((u) => {
        if (u) URL.revokeObjectURL(u);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId, pdfRequested, detail?.signed_file_path]);

  const isCreator = detail && user?.id === detail.created_by;
  const isCoo = detail && user?.id === detail.coo_approver_id;
  const isCeo = detail && user?.id === detail.ceo_approver_id;

  const formatTimeHHMM = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const handleSubmit = async () => {
    try {
      await submitSigningRequest(requestId);
      showSuccessToast("Đã trình ký.");
      await load();
      onUpdated?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi trình.");
    }
  };

  const handleApproveCoo = async () => {
    try {
      await approveCooSigningRequest(requestId);
      showSuccessToast("COO đã duyệt.");
      await load();
      onUpdated?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi duyệt.");
    }
  };

  const handleReject = async () => {
    const reason = await showInputDialog({
      title: "Từ chối yêu cầu",
      inputLabel: "Lý do từ chối",
      confirmText: "Từ chối",
    });
    if (!reason?.trim()) return;
    try {
      await rejectSigningRequest(requestId, reason.trim());
      showSuccessToast("Đã từ chối.");
      await load();
      onUpdated?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi từ chối.");
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmDialog({
      title: "Xóa yêu cầu nháp?",
      text: "File PDF đính kèm trên GCS cũng sẽ bị xóa. Thao tác này không hoàn tác được.",
      confirmText: "Xóa",
      cancelText: "Hủy",
    });
    if (!confirmed?.isConfirmed) return;
    try {
      await deleteSigningRequest(requestId);
      showSuccessToast("Đã xóa yêu cầu và file đính kèm.");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi xóa.");
    }
  };

  const handleViewPdf = async (variant) => {
    try {
      const buf = await fetchSigningPdfBlob(requestId, variant);
      const url = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      showErrorToast("Không mở được PDF.");
    }
  };

  const handleDownloadPdf = async (variant = pdfVariant) => {
    if (!requestId) return;
    const isSigned = variant === "signed";
    if (isSigned) setDownloadingSigned(true);
    try {
      const buf = await fetchSigningPdfBlob(requestId, variant);
      const url = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${detail?.request_code || "document"}${isSigned ? "-signed" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showErrorToast(isSigned ? "Không tải được file đã ký." : "Không tải được PDF.");
    } finally {
      if (isSigned) setDownloadingSigned(false);
    }
  };

  const handleDownloadSigned = () => handleDownloadPdf("signed");

  const goPdfPage = (next) => {
    if (!pdfNumPages) return;
    const clamped = Math.min(pdfNumPages, Math.max(1, next));
    setPdfPage(clamped);
    if (pdfScrollRef.current) pdfScrollRef.current.scrollTop = 0;
  };

  const changePdfZoom = (next) => {
    setPdfZoom(Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, Number(next.toFixed(2)))));
  };

  const fitPdfWidth = () => {
    const el = pdfScrollRef.current;
    if (!el) {
      setPdfZoom(1);
      return;
    }
    const available = Math.max(240, el.clientWidth - 24);
    changePdfZoom(available / PDF_BASE_WIDTH);
  };

  const hasSignedPdf = Boolean(detail?.signed_file_path);
  const pdfPageWidth = Math.round(PDF_BASE_WIDTH * pdfZoom);

  const goSign = () => {
    navigate(`/hrm/signing/${requestId}/sign`);
    onClose?.();
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
            >
              <div
                className="pointer-events-auto bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
                  <div className="min-w-0 pr-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1">
                      {detail?.request_code || "Chi tiết"}
                    </p>
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 truncate">
                      {detail?.title || "Yêu cầu trình ký"}
                    </h2>
                  </div>
                  <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 shrink-0">
                    <X size={20} />
                  </button>
                </div>

                <div className="flex-1 overflow-hidden">
                  <div className="flex flex-col lg:flex-row h-full">
                    <aside className="lg:w-[48%] lg:h-full border-b lg:border-b-0 lg:border-r border-gray-100 bg-gray-50/30 p-4 flex flex-col min-h-0 max-h-[46vh] lg:max-h-none">
                      <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                          {pdfVariant === "signed" ? "TÀI LIỆU ĐÃ KÝ" : "TÀI LIỆU PDF"}
                        </p>
                        {hasSignedPdf && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => loadPdfPreview("original")}
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-all ${
                                pdfVariant === "original"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              Gốc
                            </button>
                            <button
                              type="button"
                              onClick={() => loadPdfPreview("signed")}
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-all ${
                                pdfVariant === "signed"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              Đã ký
                            </button>
                          </div>
                        )}
                      </div>

                      {!pdfRequested ? (
                        <div className="bg-white rounded-2xl border border-gray-100 p-4">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                            PDF preview
                          </p>
                          <button
                            type="button"
                            onClick={() => setPdfRequested(true)}
                            className={btnSecondary}
                          >
                            <FileText size={14} className="inline mr-1" /> Xem file
                          </button>
                        </div>
                      ) : pdfLoading ? (
                        <p className="text-sm text-gray-400 italic">Đang tải PDF...</p>
                      ) : pdfUrl ? (
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col flex-1 min-h-0">
                          {/* Nhóm 1 (top, canh giữa): zoom / xoay / mở / tải */}
                          <div className="flex justify-center items-center gap-1.5 px-2.5 py-2 border-b border-gray-100 bg-gradient-to-r from-red-50/40 via-white to-white shrink-0">
                            <button
                              type="button"
                              className={pdfToolBtn}
                              disabled={pdfZoom <= PDF_ZOOM_MIN}
                              onClick={() => changePdfZoom(pdfZoom - PDF_ZOOM_STEP)}
                              title="Thu nhỏ"
                            >
                              <ZoomOut size={15} />
                            </button>
                            <button
                              type="button"
                              className="h-8 min-w-[3.25rem] px-2 rounded-lg border border-gray-200 bg-white text-[11px] font-bold tabular-nums text-gray-600 hover:border-red-300 hover:text-red-600"
                              onClick={() => setPdfZoom(1)}
                              title="Đặt lại 100%"
                            >
                              {Math.round(pdfZoom * 100)}%
                            </button>
                            <button
                              type="button"
                              className={pdfToolBtn}
                              disabled={pdfZoom >= PDF_ZOOM_MAX}
                              onClick={() => changePdfZoom(pdfZoom + PDF_ZOOM_STEP)}
                              title="Phóng to"
                            >
                              <ZoomIn size={15} />
                            </button>
                            <button
                              type="button"
                              className={pdfToolBtn}
                              onClick={fitPdfWidth}
                              title="Vừa khung"
                            >
                              <Maximize2 size={14} />
                            </button>
                            <button
                              type="button"
                              className={pdfToolBtn}
                              onClick={() => setPdfRotation((r) => (r + 90) % 360)}
                              title="Xoay 90°"
                            >
                              <RotateCw size={14} />
                            </button>
                            <span className="w-px h-5 bg-gray-200 mx-0.5" />
                            <button
                              type="button"
                              className={pdfToolBtn}
                              onClick={() => handleViewPdf(pdfVariant)}
                              title="Mở tab mới"
                            >
                              <ExternalLink size={14} />
                            </button>
                            <button
                              type="button"
                              className={pdfToolBtn}
                              onClick={() => handleDownloadPdf(pdfVariant)}
                              disabled={pdfVariant === "signed" && downloadingSigned}
                              title="Tải PDF"
                            >
                              <Download size={14} />
                            </button>
                          </div>

                          <div
                            ref={pdfScrollRef}
                            className="flex-1 overflow-auto p-3 bg-slate-50/80 min-h-[280px]"
                          >
                            <Document
                              file={pdfUrl}
                              onLoadSuccess={({ numPages }) => {
                                setPdfNumPages(numPages);
                                setPdfPage((p) => Math.min(Math.max(1, p), numPages));
                              }}
                              loading={
                                <p className="text-sm text-gray-400 italic">Đang render...</p>
                              }
                              error={
                                <p className="text-sm text-red-500 italic">
                                  Không render được PDF. Thử mở tab mới.
                                </p>
                              }
                            >
                              <Page
                                pageNumber={pdfPage}
                                width={pdfPageWidth}
                                rotate={pdfRotation}
                                className="mx-auto shadow-sm"
                                renderTextLayer
                                renderAnnotationLayer
                              />
                            </Document>
                          </div>

                          {/* Nhóm 2 (bottom, canh giữa): phân trang */}
                          <div className="flex justify-center items-center gap-1.5 px-2.5 py-2 border-t border-gray-100 bg-white shrink-0">
                            <button
                              type="button"
                              className={pdfToolBtn}
                              disabled={!pdfNumPages || pdfPage <= 1}
                              onClick={() => goPdfPage(pdfPage - 1)}
                              title="Trang trước"
                            >
                              <ChevronLeft size={16} />
                            </button>
                            <div className="inline-flex items-center gap-1 px-1.5 h-8 rounded-lg border border-gray-200 bg-white text-[11px] font-semibold text-gray-600">
                              <input
                                type="number"
                                min={1}
                                max={pdfNumPages || 1}
                                value={pdfPage}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!Number.isNaN(v)) goPdfPage(v);
                                }}
                                className="w-10 text-center bg-transparent outline-none tabular-nums"
                                aria-label="Số trang"
                              />
                              <span className="text-gray-400">/</span>
                              <span className="tabular-nums pr-1">{pdfNumPages || "—"}</span>
                            </div>
                            <button
                              type="button"
                              className={pdfToolBtn}
                              disabled={!pdfNumPages || pdfPage >= pdfNumPages}
                              onClick={() => goPdfPage(pdfPage + 1)}
                              title="Trang sau"
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Không có PDF để hiển thị.</p>
                      )}

                      {hasSignedPdf && (
                        <div className="mt-3 flex flex-wrap gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={handleDownloadSigned}
                            disabled={downloadingSigned}
                            className={btnSecondary}
                          >
                            <Download size={14} className="inline mr-1" />
                            {downloadingSigned ? "Đang tải..." : "Tải file đã ký"}
                          </button>
                        </div>
                      )}
                    </aside>

                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                  {loading ? (
                    <p className="text-center text-sm text-gray-400 italic py-8">Đang tải...</p>
                  ) : detail ? (
                    <>
                      <div className="flex flex-wrap gap-2 items-center">
                        <span
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                            STATUS_BADGE[detail.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {STATUS_LABEL[detail.status] || detail.status}
                        </span>
                        {detail.document_type && (
                          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-white text-gray-700 border-gray-200">
                            {DOCUMENT_TYPE_LABEL[detail.document_type] || detail.document_type}
                          </span>
                        )}
                        {detail.urgency && detail.urgency !== "normal" && (
                          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full border bg-amber-100 text-amber-800 border-amber-200">
                            {URGENCY_LABEL[detail.urgency]}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 font-medium">
                          Người ký: <strong className="text-gray-700">{detail.signer_role}</strong>
                        </span>
                      </div>

                      <div className="rounded-2xl border border-gray-100 bg-gray-50/70 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                            Thông tin tài liệu
                          </p>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {detail.document_number && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Số hiệu</span>
                              <span className="font-semibold text-gray-800 font-mono text-xs">{detail.document_number}</span>
                            </div>
                          )}
                          {detail.document_type && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Loại VB</span>
                              <span className="font-semibold text-gray-800">
                                {DOCUMENT_TYPE_LABEL[detail.document_type] || detail.document_type}
                              </span>
                            </div>
                          )}
                          {detail.issued_date && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Ngày ban hành</span>
                              <span className="font-semibold text-gray-800">{formatDateVN(detail.issued_date)}</span>
                            </div>
                          )}
                          {detail.effective_until && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Hết hiệu lực</span>
                              <span className="font-semibold text-gray-800">{formatDateVN(detail.effective_until)}</span>
                            </div>
                          )}
                          {detail.issuing_department && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Phòng ban BH</span>
                              <span className="font-semibold text-gray-800">{detail.issuing_department}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                            <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Người tạo</span>
                            <span className="font-semibold text-gray-800">{detail.created_by_name}</span>
                          </div>
                          <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                            <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Ngày tạo</span>
                            <span className="font-semibold text-gray-800">
                              {formatDateVN(detail.created_at)} {formatTimeHHMM(detail.created_at)}
                            </span>
                          </div>
                          {detail.signed_by_name && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Người ký</span>
                              <span className="font-semibold text-gray-800">{detail.signed_by_name}</span>
                            </div>
                          )}
                          {detail.signed_at && (
                            <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3 text-sm">
                              <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Ký lúc</span>
                              <span className="font-semibold text-gray-800">
                                {formatDateVN(detail.signed_at)} {formatTimeHHMM(detail.signed_at)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {detail.description && (
                        <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                            Nội dung
                          </p>
                          <p className="text-sm leading-6 text-gray-700">{detail.description}</p>
                        </div>
                      )}

                      {detail.reject_reason && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                          <strong>Lý do từ chối:</strong> {detail.reject_reason}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setPdfRequested(true)}
                          className={`${btnSecondary} lg:hidden`}
                        >
                          <FileText size={14} className="inline mr-1" /> Xem file
                        </button>
                        {/* Không hiển thị nút PDF đã ký */}
                      </div>

                      {detail.logs?.length > 0 && (
                        <div>
                          <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                            Lịch sử
                          </h3>
                          <ul className="space-y-2">
                            {detail.logs.map((log) => (
                              <li
                                key={log.id}
                                className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-600 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100"
                              >
                                <span className="text-gray-400 shrink-0">{formatDateVN(log.created_at)}</span>
                                <span className="font-semibold">{ACTION_LABEL[log.action] || log.action}</span>
                                {log.actor_name && <span>— {log.actor_name}</span>}
                                {log.note && <span className="text-gray-400 italic">({log.note})</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {detail.recipients?.length > 0 && (
                        <div>
                          <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                            Email đã gửi
                          </h3>
                          <ul className="space-y-1">
                            {detail.recipients.map((r) => (
                              <li
                                key={r.id}
                                className="flex justify-between text-xs py-2 px-3 rounded-xl bg-gray-50 border border-gray-100"
                              >
                                <span className="font-medium text-gray-700">{r.employee_name}</span>
                                <span className={r.email_status === "sent" ? "text-emerald-600 font-bold" : "text-red-500"}>
                                  {r.email_status}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
                  </div>
                </div>

                {detail && (
                  <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    {isCreator && detail.status === "draft" && (
                      <>
                        <button type="button" onClick={handleDelete} className={btnDanger}>
                          <Trash2 size={14} /> Xóa
                        </button>
                        <button type="button" onClick={handleSubmit} className={btnPrimary}>
                          <Send size={14} /> Trình ký
                        </button>
                      </>
                    )}
                    {isCoo && detail.status === "pending_approve_coo" && (
                      <>
                        <button type="button" onClick={handleReject} className={btnDanger}>
                          <X size={14} /> Từ chối
                        </button>
                        <button type="button" onClick={handleApproveCoo} className={btnPrimary}>
                          <Check size={14} /> Duyệt trình CEO
                        </button>
                      </>
                    )}
                    {isCoo && detail.status === "pending_sign_coo" && (
                      <>
                        <button type="button" onClick={handleReject} className={btnDanger}>
                          <X size={14} /> Từ chối
                        </button>
                        <button type="button" onClick={goSign} className={btnPrimary}>
                          <PenLine size={14} /> Ký văn bản
                        </button>
                      </>
                    )}
                    {isCeo && detail.status === "pending_sign_ceo" && (
                      <>
                        <button type="button" onClick={handleReject} className={btnDanger}>
                          <X size={14} /> Từ chối
                        </button>
                        <button type="button" onClick={goSign} className={btnPrimary}>
                          <PenLine size={14} /> Ký văn bản
                        </button>
                      </>
                    )}
                    {isCreator && detail.status === "signed" && (
                      <button type="button" onClick={() => setShowNotify(true)} className={btnPrimary}>
                        <Mail size={14} /> Gửi email thông báo
                      </button>
                    )}
                    {isCreator && hasSignedPdf && ["signed", "completed"].includes(detail.status) && (
                      <button type="button" onClick={() => setShowSendEmail(true)} className={btnSecondary}>
                        <Mail size={14} /> Gửi email
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <SigningNotifyModal
        open={showNotify}
        requestId={requestId}
        onClose={() => setShowNotify(false)}
        onSent={() => {
          setShowNotify(false);
          load();
          onUpdated?.();
        }}
      />

      <SigningSendEmailModal
        open={showSendEmail}
        requestId={requestId}
        requestTitle={detail?.title}
        requestCode={detail?.request_code}
        onClose={() => setShowSendEmail(false)}
        onSent={() => {
          setShowSendEmail(false);
          load();
          onUpdated?.();
        }}
      />
    </>
  );
};

export default SigningRequestDetailModal;
